// Column filter matching that forgives punctuation and formatting, so what a
// user types does not have to be character for character what the cell shows.
//
// - text:   case and punctuation insensitive ("cve cn" finds "ATH CN/CVE CN")
// - date:   "10-27", "10/27", "1027", "2026-10", "10/27/2026" all find 2026-10-27
// - number: "5760" finds 5,760 and (5,760); "101.3" finds $101.36

const alnum = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const digits = (value) => String(value ?? "").replace(/\D/g, "");

export const textMatches = (cellValue, filterValue) => {
  const needle = alnum(filterValue);
  return needle === "" || alnum(cellValue).includes(needle);
};

// iso is "YYYY-MM-DD". Matches the filter digits against year-first and
// month-first spellings of the date.
export const dateMatches = (iso, filterValue) => {
  const needle = digits(filterValue);
  if (String(filterValue ?? "").trim() === "") return true;
  if (needle === "") return false;
  const [y = "", m = "", d = ""] = String(iso ?? "").split("-");
  if (!y || !m || !d) return false;
  return [`${y}${m}${d}`, `${m}${d}${y}`, `${m}${d}${y.slice(2)}`].some((s) =>
    s.includes(needle),
  );
};

// Compares on digits only, so signs, commas, brackets and currency symbols
// in what is shown never get in the way.
export const numberMatches = (cellValue, filterValue) => {
  if (String(filterValue ?? "").trim() === "") return true;
  const needle = digits(filterValue);
  if (needle === "") return false;
  const n = Math.abs(Number(cellValue));
  if (!Number.isFinite(n)) return textMatches(cellValue, filterValue);
  return [String(n), n.toFixed(2), n.toFixed(4)].some((s) =>
    digits(s).includes(needle),
  );
};

// TanStack/MRT filter functions: (row, columnId, filterValue) => boolean.
export const smartTextFilterFn = (row, columnId, filterValue) =>
  textMatches(row.getValue(columnId), filterValue);

export const smartNumberFilterFn = (row, columnId, filterValue) =>
  numberMatches(row.getValue(columnId), filterValue);

export const makeDateFilterFn = (getIsoDate) => (row, _columnId, filterValue) =>
  dateMatches(getIsoDate(row.original), filterValue);

// Global (search box) filter for tables that group rows with
// groupedColumnMode "remove". The table library drops grouped columns from the
// columns it searches, so a search can never match what the rows are grouped
// by. This also checks those fields on the row itself. Called once per
// searchable column and OR-ed by the library, so one true is enough.
export const makeGroupedGlobalFilterFn = (groupedFields) => (row, columnId, filterValue) =>
  smartTextFilterFn(row, columnId, filterValue) ||
  groupedFields.some((field) => textMatches(row.original?.[field], filterValue));
